import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";

const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    overline: "Legal · Draft for review",
    updated: "Last updated: October 2026",
    sections: [
      {
        h: "Who we are",
        b: "Orphion Global HR is a brand of Nivyan Business Solutions LLP, Bengaluru, India. This policy explains how we collect, use and protect personal data on orphionglobalhr.com, in line with India's Digital Personal Data Protection (DPDP) Act and, where applicable, the GDPR.",
      },
      {
        h: "What we collect",
        b: "Contact details you submit through our forms (name, work email, company, phone, message); candidate information you share via the CV upload (parsed to pre-fill your profile at your request); diagnostic-call details; and newsletter subscriptions. We do not collect special-category data through this site.",
      },
      {
        h: "How we use it",
        b: "To respond to enquiries, run diagnostic calls, match candidate profiles to open roles, send requested alerts and newsletters, and operate the site. We do not sell personal data and do not share it with third parties except the processors that run the site (hosting, email delivery), under contract.",
      },
      {
        h: "Your rights",
        b: "You may request access, correction or deletion of your personal data at any time by writing to sales@orphionglobalhr.com. Newsletter and alert emails include a one-click unsubscribe link.",
      },
      {
        h: "Retention & security",
        b: "Data is stored on secured infrastructure with access limited to the delivery team. Enquiry and candidate records are retained while an engagement or hiring process is active and removed on request.",
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    overline: "Legal · Draft for review",
    updated: "Last updated: October 2026",
    sections: [
      {
        h: "The site",
        b: "orphionglobalhr.com is the marketing site of Orphion Global HR, a brand of Nivyan Business Solutions LLP. Content is provided for general information and does not constitute professional HR, legal or tax advice.",
      },
      {
        h: "Engagements",
        b: "Any recruitment, consulting, operations or fractional-leadership engagement is governed by a separate written agreement — scope, fees, timelines and guarantees are defined there, not on this site.",
      },
      {
        h: "Candidate submissions",
        b: "By submitting a CV you confirm the information is accurate and that you consent to being contacted about matching roles. Job postings describe client mandates and may change or close without notice.",
      },
      {
        h: "Liability",
        b: "The site is provided as-is. To the extent permitted by law, Nivyan Business Solutions LLP is not liable for decisions made in reliance on site content.",
      },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    overline: "Legal · Draft for review",
    updated: "Last updated: October 2026",
    sections: [
      {
        h: "What we use",
        b: "This site uses a small number of cookies: a session cookie if you sign in to the admin or hiring-partner areas, and local storage for features you invoke (saved job searches, your CV profile). No advertising or cross-site tracking cookies are set.",
      },
      {
        h: "Analytics",
        b: "If analytics are enabled, they load only after consent. You can decline without losing access to any part of the site.",
      },
      {
        h: "Managing cookies",
        b: "You can clear or block cookies in your browser settings at any time; the public site works fully without them.",
      },
    ],
  },
};

export default function LegalPage({ doc }) {
  const page = LEGAL[doc];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);
  if (!page) return <Navigate to="/" replace />;

  return (
    <div className="bg-[var(--bg)] min-h-screen">
      <Seo title={`${page.title} — Orphion Global HR`} description={`${page.title} for orphionglobalhr.com`} path={`/${doc}`} />
      <PageHero
        overline={page.overline}
        titleA={page.title.replace(/ (Policy|Use)$/, "")}
        titleAccent={page.title.match(/ (Policy|Use)$/)?.[0]?.trim() || ""}
        titleB=""
        intro={page.updated}
      />
      <section className="py-16 md:py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl space-y-12">
            {page.sections.map((s) => (
              <div key={s.h}>
                <h2 className="font-display text-2xl tracking-tight text-[var(--ink)] mb-3">{s.h}</h2>
                <p className="text-[var(--ink-80)] leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
